import React, { Component } from 'react';
import Dropzone from 'react-dropzone';
import Swal from 'sweetalert2';
import { appDatabasePrimaryFunctions } from '../../base';
import UploadImage from '../utils/UploadImage';
import { ErrorMessage, Toast } from '../utils/HelpfulFunction';
import { loginIsActive } from '../utils/featureAvailability';
import { registerUnsavedChanges } from '../utils/unsavedChanges';
import {
    CHOOSER_NODE,
    DEFAULT_TITLE,
    TITLE_MAX,
    MIN_BUTTONS,
    emptyDraft,
    draftFromNode,
    nodeFromDraft,
    isDirty,
    moveButton,
    addButton,
    withoutButton,
    withButtonImage,
    addableExperiences,
    saveProblem,
    chooserStatus,
    chooserWarnings,
    landingExperience,
    isLiveExperience,
    stampOf,
    savedSince,
    isAcceptableImageUrl,
    UPLOAD_TIMEOUT_MS,
    previewLayout,
    PREVIEW_PHONES,
    SCREEN_LAYOUT,
    TILE_GAP,
    TILE_ASPECT_RATIO,
    TILE_ART,
    BACKGROUND_ART
} from '../utils/experienceChooser';
import '../../styles/css/AdminMain.css';
import '../../styles/css/DropzoneCSS.css';

// The "Choose your Experience" screen's editor, drawn under the experience
// list on the Experiences page.
//
// It is its own component, with its own load and save, because it is its own
// node: one root `experienceChooser` for the whole club, not a field of any
// experience. See utils/experienceChooser.js for the shape and the reasons.
//
// Read once and saved as the whole node in one write — the Experiences page
// reads with once() and writes explicitly, and this matches it rather than
// the live bindings older screens use, so an operator's half-finished edit is
// never overwritten by a reload underneath them. The write is a transaction
// that refuses to land if anyone else has saved since this page read the
// screen (see save()), so the whole-node write cannot silently undo them.
//
// Whether fans actually GET the screen is a Feature Access switch, and this
// editor deliberately does not duplicate it: two switches for one thing is
// how a club ends up with them disagreeing. It reports what that switch (and
// Change Experience, which the fan app gates it on) currently add up to, and
// links there.

// UploadImage refuses anything over 2.2 MB, so the drop target does too —
// otherwise a file is accepted here only to be refused a second later.
const UPLOAD_MAX_BYTES = 2200000;
const IMAGE_ACCEPT = { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] };
const BACKGROUND = 'background';
const buttonUploadKey = (experienceId) => `button:${experienceId}`;

const uploadTimeoutMessage = (ms) => {
    const seconds = Math.max(1, Math.round(ms / 1000));
    return `The upload took longer than ${seconds} second${seconds === 1 ? '' : 's'}, so it was stopped. Check your connection and try again.`;
};

export const CONFLICT_MESSAGE = 'Someone else saved this screen since you opened it — reload to see their changes; your edits are still here.';

/**
 * Runs `work(ref)` while a value listener is attached to `ref`, once the
 * listener has the server's value.
 *
 * For the save's transaction. Firebase runs a transaction's update first
 * against its LOCAL copy of the node, and with nothing listening there is no
 * local copy: the first call sees null. The usual answer — propose something
 * anyway and let the server send the real value back — does not work for an
 * update that aborts on "the screen has gone missing", because it would
 * abort on that first null and never ask the server. With a listener holding
 * the node, the first call sees what the server has, and the server still
 * rejects the write if the node changes in between (the SDK then re-runs the
 * update with the newer value). A refused read rejects, like any failure.
 */
function whileListening(ref, work) {
    return new Promise((resolve, reject) => {
        let started = false;
        const onValue = () => {
            if (started) return;
            started = true;
            Promise.resolve()
                .then(() => work(ref))
                .then(resolve, reject)
                .finally(() => ref.off('value', onValue));
        };
        ref.on('value', onValue, (error) => {
            if (started) return;
            started = true;
            reject(error);
        });
    });
}

class ExperienceChooserSetup extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // 'loading' | 'ready' | 'failed'. Nothing can be saved until the
            // stored screen has been read: a save from an empty draft would
            // wipe whatever the club had set up.
            status: 'loading',
            // What is in the database, as last read or saved, and what is on
            // screen. Save is offered only when they differ.
            baseline: emptyDraft(),
            draft: emptyDraft(),
            // The stored screen's updatedAt as this page last read or wrote
            // it (null: there was none) — the version the draft is based on.
            savedStamp: null,
            // Set when a save found someone else's newer save in the way.
            conflict: false,
            // Buttons read back for experiences that no longer exist.
            orphaned: [],
            // Null until read. The status line is advice — a failed read
            // leaves it "unknown" rather than blocking the editor.
            flags: null,
            access: null,
            // Whether fans can sign in at all (root settings). Undefined until
            // read, which counts as yes.
            loginActive: undefined,
            flagsFailed: false,
            saving: false,
            uploading: null,
            addChoice: '',
            // Which of PREVIEW_PHONES the preview draws.
            previewPhone: PREVIEW_PHONES[0].id,
            // The last reorder, as a sentence, for the polite live region.
            announcement: ''
        };
        this.uploader = new UploadImage();
        // Each tile's chevrons, by experience id: { earlier, later }.
        this.orderButtons = {};
        this.orderRefCallbacks = {};
        this.save = this.save.bind(this);
        this.handleAdd = this.handleAdd.bind(this);
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    }

    componentDidMount() {
        // Independent reads: the screen's own node is refused on a club whose
        // rules predate it, and that must not leave the status line saying
        // "Checking…" for ever — the switches are readable either way.
        this.loadScreen();
        this.loadSwitches();
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        // beforeunload does not cover the side menu's Log out, which leaves
        // without unloading; that asks this registry instead.
        this.unregisterUnsaved = registerUnsavedChanges({
            label: 'the Choose your Experience screen',
            isDirty: () => this.hasUnsavedChanges()
        });
    }

    componentWillUnmount() {
        this.unmounted = true;
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        if (this.unregisterUnsaved) this.unregisterUnsaved();
    }

    async loadScreen() {
        let stored;
        try {
            const snapshot = await appDatabasePrimaryFunctions.ref(CHOOSER_NODE).once('value');
            stored = snapshot.val();
        } catch (e) {
            // Also what a club sees whose database rules predate this screen:
            // an undeclared node is refused to everyone.
            console.log('Error loading the Choose your Experience screen:', e);
            if (!this.unmounted) this.setState({ status: 'failed' });
            return;
        }
        if (this.unmounted) return;
        const { draft, orphaned } = draftFromNode(stored, this.props.experiences);
        this.setState({ status: 'ready', baseline: draft, draft, orphaned, savedStamp: stampOf(stored) });
    }

    async loadSwitches() {
        try {
            const [flagsSnap, accessSnap, settingsSnap] = await Promise.all([
                appDatabasePrimaryFunctions.ref('featureFlags').once('value'),
                appDatabasePrimaryFunctions.ref('featureAccess').once('value'),
                appDatabasePrimaryFunctions.ref('settings').once('value')
            ]);
            if (!this.unmounted) {
                this.setState({
                    flags: flagsSnap.val() || {},
                    access: accessSnap.val() || {},
                    loginActive: loginIsActive(settingsSnap.val())
                });
            }
        } catch (e) {
            console.log('Error loading feature switches for the Choose your Experience screen:', e);
            if (!this.unmounted) this.setState({ flagsFailed: true });
        }
    }

    hasUnsavedChanges() {
        return this.state.status === 'ready' && isDirty(this.state.draft, this.state.baseline);
    }

    handleBeforeUnload(event) {
        if (this.hasUnsavedChanges()) {
            const message = 'You have unsaved changes. Are you sure you want to leave?';
            event.preventDefault();
            event.returnValue = message;
            return message;
        }
    }

    // Whether the screen as this editor last read or saved it has a button
    // for this experience. For the Experiences page's delete dialog.
    hasStoredButton(experienceId) {
        return this.state.status === 'ready' && this.state.baseline.buttons.some(button => button.id === experienceId);
    }

    // Called by the Experiences page after it deletes an experience. The
    // button comes out of what is on screen, so a later Save cannot put it
    // back — which the rules would refuse anyway, failing the whole save for
    // a button nobody can see.
    //
    // `storedButtonGone`: the delete removed the stored button in the same
    // atomic update, or the database said there was none. Then it comes out
    // of the saved baseline too. Otherwise (the page could not read that
    // path) the stored button is most likely still there, pointing at an
    // experience that no longer exists — exactly an orphan, so it is listed
    // as one: the editor says so, and Save is offered to clear it.
    forgetExperience(experienceId, { storedButtonGone = true } = {}) {
        this.setState(state => {
            const wasStored = state.baseline.buttons.some(button => button.id === experienceId);
            const orphaned = !storedButtonGone && wasStored && !state.orphaned.includes(experienceId)
                ? [...state.orphaned, experienceId].sort()
                : state.orphaned;
            return {
                draft: { ...state.draft, buttons: withoutButton(state.draft.buttons, experienceId) },
                baseline: { ...state.baseline, buttons: withoutButton(state.baseline.buttons, experienceId) },
                orphaned,
                addChoice: state.addChoice === experienceId ? '' : state.addChoice
            };
        });
    }

    updateDraft(change) {
        this.setState(state => ({ draft: { ...state.draft, ...change(state.draft) } }));
    }

    handleAdd() {
        const id = this.state.addChoice;
        if (!id) return;
        this.updateDraft(draft => ({ buttons: addButton(draft.buttons, id) }));
        this.setState({ addChoice: '' });
    }

    // Swap a tile with its neighbour, say so to screen readers, and keep
    // focus on a chevron that still does something. A tile that reaches an
    // end disables the chevron just pressed, and a disabled button drops focus
    // to the page — so focus moves to the tile's other chevron instead.
    move(index, direction) {
        const buttons = this.state.draft.buttons;
        const target = index + direction;
        if (index < 0 || index >= buttons.length || target < 0 || target >= buttons.length) return;
        const moved = buttons[index];
        const count = buttons.length;
        const name = ((this.props.experiences || {})[moved.id] || {}).name || moved.id;
        this.setState(state => ({
            draft: { ...state.draft, buttons: moveButton(state.draft.buttons, index, direction) },
            announcement: `Moved ${name} to position ${target + 1} of ${count}`
        }), () => {
            const atEnd = direction < 0 ? target === 0 : target === count - 1;
            const chevrons = this.orderButtons[moved.id] || {};
            const pressed = direction < 0 ? chevrons.earlier : chevrons.later;
            const other = direction < 0 ? chevrons.later : chevrons.earlier;
            const next = atEnd ? other : pressed;
            if (next && typeof next.focus === 'function') next.focus();
        });
    }

    // Ref callbacks for each tile's two chevrons, kept per tile so a move can
    // put focus back on the right one after React has reordered the tiles.
    orderRef(experienceId, which) {
        const key = `${experienceId}:${which}`;
        if (!this.orderRefCallbacks[key]) {
            this.orderRefCallbacks[key] = (element) => {
                this.orderButtons[experienceId] = { ...(this.orderButtons[experienceId] || {}), [which]: element };
            };
        }
        return this.orderRefCallbacks[key];
    }

    async removeButton(experienceId) {
        const exp = (this.props.experiences || {})[experienceId] || {};
        const result = await Swal.fire({
            title: `Remove the ${exp.name || experienceId} button?`,
            text: 'Fans stop seeing it on the Choose your Experience screen once you save. The experience itself is not touched.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove'
        });
        if (!result.value) return;
        this.updateDraft(draft => ({ buttons: withoutButton(draft.buttons, experienceId) }));
    }

    async uploadInto(key, accepted, rejected, apply) {
        if ((rejected && rejected.length > 0) || !accepted || accepted.length === 0) {
            ErrorMessage.fire({
                title: 'Image cannot be uploaded',
                text: 'Use a JPG or PNG under 2 MB.'
            });
            return;
        }
        // Save and every drop target wait while an upload runs, so an upload
        // that never answers would lock the editor until a reload. The
        // uploader aborts its request at the limit; this editor also stops
        // waiting at the same moment, in case a request ignores the abort,
        // and ignores anything that arrives after it has given up.
        const attempt = (this.uploadAttempt = (this.uploadAttempt || 0) + 1);
        const timeoutMs = this.props.uploadTimeoutMs;
        this.setState({ uploading: key });
        let res;
        let timer;
        try {
            res = await Promise.race([
                this.uploader.upload_file(accepted[0], { timeoutMs }),
                new Promise(resolve => {
                    timer = setTimeout(() => resolve({ error: uploadTimeoutMessage(timeoutMs), timedOut: true }), timeoutMs);
                })
            ]);
        } catch (e) {
            res = { error: e.message };
        } finally {
            clearTimeout(timer);
        }
        if (this.unmounted || attempt !== this.uploadAttempt) return;
        this.setState({ uploading: null });
        if (!res || res.error || !res.imageUrl) {
            ErrorMessage.fire({
                title: 'Image cannot be uploaded',
                text: (res && res.error) || 'Something went wrong, please upload the image again.'
            });
            return;
        }
        // The uploader answers with the address as plain text. Only an https
        // one is usable: the rules refuse anything else, and phones would not
        // draw it — better refused now, on the image, than at Save.
        const imageUrl = String(res.imageUrl).trim();
        if (!isAcceptableImageUrl(imageUrl)) {
            ErrorMessage.fire({
                title: 'Image cannot be used',
                text: 'The upload service answered with an address that is not a secure (https) link, so phones would not show it. Please upload the image again.'
            });
            return;
        }
        // A button removed while its image was uploading simply isn't there
        // to receive it; withButtonImage matches nothing.
        this.updateDraft(draft => apply(draft, imageUrl));
    }

    // Save has something to do: an edit, or a stored button for an experience
    // that is gone, which only a save clears out.
    canSave() {
        return this.hasUnsavedChanges() || this.state.orphaned.length > 0;
    }

    async save() {
        if (this.state.saving || this.state.uploading || !this.canSave()) return;
        const problem = saveProblem(this.state.draft, this.props.experiences);
        if (problem) {
            ErrorMessage.fire({ title: 'Cannot save yet', text: problem });
            return;
        }
        this.setState({ saving: true });

        // Checked against the database rather than the list this page read: an
        // experience deleted since, in another tab or by another admin, leaves a
        // button the rules refuse — and a refused button fails the whole save,
        // every time, for a tile nobody can see. An empty or failed read falls
        // back to the page's own list rather than dropping every button.
        let known = this.props.experiences || {};
        try {
            const snapshot = await appDatabasePrimaryFunctions.ref('experiences').once('value');
            const fresh = snapshot.val();
            if (fresh && typeof fresh === 'object' && Object.keys(fresh).length > 0) known = fresh;
        } catch (e) {
            console.log('Error re-reading experiences before saving the Choose your Experience screen:', e);
        }
        if (this.unmounted) return;
        const gone = this.state.draft.buttons.filter(button => !known[button.id]).map(button => button.id);
        const draftToSave = { ...this.state.draft, buttons: this.state.draft.buttons.filter(button => known[button.id]) };
        const node = nodeFromDraft(draftToSave, Date.now());
        const loadedStamp = this.state.savedStamp;

        let result;
        try {
            // The whole node, in one write: orders renumbered 1..n, removed
            // buttons gone, blank fields absent — but only if the screen is
            // still the version this page read. A transaction, so the check
            // and the write are one step on the server: returning undefined
            // aborts it, and nothing is written.
            result = await whileListening(appDatabasePrimaryFunctions.ref(CHOOSER_NODE), ref =>
                ref.transaction(current => (savedSince(current, loadedStamp) ? undefined : node), undefined, false));
        } catch (e) {
            console.log('Error saving the Choose your Experience screen:', e);
            if (!this.unmounted) this.setState({ saving: false });
            ErrorMessage.fire({
                title: 'Could not save',
                text: 'Something went wrong saving the Choose your Experience screen. Check your connection and try again; if it keeps failing, refresh the page — your changes here will need redoing.'
            });
            return;
        }
        if (this.unmounted) return;
        if (!result || !result.committed) {
            // Someone else's save is newer than what this page is editing.
            // Writing the draft over it would quietly undo their changes, so
            // nothing is written and the draft stays exactly as it is.
            this.setState({ saving: false, conflict: true });
            ErrorMessage.fire({ title: 'Not saved', text: CONFLICT_MESSAGE });
            return;
        }
        const { draft } = draftFromNode(node, known);
        this.setState({ baseline: draft, draft, orphaned: [], saving: false, savedStamp: stampOf(node), conflict: false });
        Toast.fire({
            title: gone.length > 0
                ? `Saved — left out ${gone.length === 1 ? 'a button' : `${gone.length} buttons`} for a deleted experience (${gone.join(', ')})`
                : 'Choose your Experience screen saved'
        });
    }

    statusLine(status) {
        if (this.state.flagsFailed) return { label: 'Unknown', detail: 'The feature switches could not be read.' };
        if (!status) return { label: 'Checking…', detail: '' };
        if (status.state === 'everyone') {
            return {
                label: status.label,
                detail: status.except > 0 ? `except ${status.except} ${status.except === 1 ? 'person' : 'people'} set to off` : ''
            };
        }
        if (status.state === 'testers') {
            return { label: status.label, detail: `${status.testers} ${status.testers === 1 ? 'person' : 'people'}` };
        }
        return { label: status.label, detail: '' };
    }

    renderTile(button, index, count, locked) {
        const exp = (this.props.experiences || {})[button.id] || {};
        const name = exp.name || button.id;
        const uploadingThis = this.state.uploading === buttonUploadKey(button.id);
        const action = button.imageUrl ? 'Replace' : 'Upload';
        return (
            <div key={button.id} className="admin-tile">
                <Dropzone
                    onDrop={(accepted, rejected) => this.uploadInto(buttonUploadKey(button.id), accepted, rejected,
                        (draft, url) => ({ buttons: withButtonImage(draft.buttons, button.id, url) }))}
                    accept={IMAGE_ACCEPT}
                    multiple={false}
                    maxSize={UPLOAD_MAX_BYTES}
                    disabled={locked || !!this.state.uploading}
                >
                    {({ getRootProps, getInputProps, isDragActive }) => (
                        <div {...getRootProps({
                            className: `admin-tile-art is-7x5 is-droppable${isDragActive ? ' is-dragging' : ''}`,
                            role: 'button',
                            'aria-label': `${action} the image for ${name}`,
                            title: `${action} the image for ${name}`
                        })}>
                            <input {...getInputProps()} />
                            {button.imageUrl
                                ? <img src={button.imageUrl} alt="" />
                                : <div className="admin-tile-empty">No image yet</div>}
                            <span className="admin-tile-art-cta">{uploadingThis ? 'Uploading…' : `${action} image`}</span>
                        </div>
                    )}
                </Dropzone>
                <div className="admin-tile-body">
                    <p className="admin-tile-name">
                        {name}
                        {exp.live === false &&
                            <span className="admin-tag" style={{ marginLeft: 6 }} title="Fans do not see this button until the experience is published.">Draft</span>
                        }
                    </p>
                    <p className="admin-tile-link" title={button.id}>{button.id}</p>
                </div>
                <div className="admin-tile-foot">
                    {count > 1 &&
                        <span className="admin-tile-order">
                            <button
                                type="button"
                                className="admin-order-btn"
                                ref={this.orderRef(button.id, 'earlier')}
                                aria-label={`Move ${name} earlier`}
                                title="Move earlier"
                                disabled={locked || index === 0}
                                onClick={() => this.move(index, -1)}
                            >
                                <span className="fa fa-chevron-left" aria-hidden="true"/>
                            </button>
                            {/* A bare number means nothing read aloud. */}
                            <b><span className="visually-hidden">Position </span>{index + 1}<span className="visually-hidden"> of {count}</span></b>
                            <button
                                type="button"
                                className="admin-order-btn"
                                ref={this.orderRef(button.id, 'later')}
                                aria-label={`Move ${name} later`}
                                title="Move later"
                                disabled={locked || index === count - 1}
                                onClick={() => this.move(index, 1)}
                            >
                                <span className="fa fa-chevron-right" aria-hidden="true"/>
                            </button>
                        </span>
                    }
                    <span className="admin-row-actions">
                        <button type="button" className="admin-icon-btn is-danger" aria-label={`Remove the ${name} button`} title="Remove button" disabled={locked} onClick={() => this.removeButton(button.id)}><span className="fa fa-trash-o" aria-hidden="true"/></button>
                    </span>
                </div>
            </div>
        );
    }

    renderPreview(draft, known) {
        // What the fan app draws: drafts and vanished experiences left out,
        // in button order, each tile 7:5 and filled the way the app fills it.
        //
        // Sized by the app's own rule (previewLayout → tileWidthFor, the
        // contract's fit-to-height formula with the constants the cross-repo
        // test pins), in POINTS on a modelled phone. The numbers reach the
        // stylesheet as custom properties, and AdminMain.css turns a point into
        // a fraction of the preview's width — so the CSS holds no sizes of its
        // own to drift from the app.
        const tiles = draft.buttons.filter(button => isLiveExperience(known[button.id]));
        const title = String(draft.title || '').trim() || DEFAULT_TITLE;
        const phone = PREVIEW_PHONES.find(p => p.id === this.state.previewPhone) || PREVIEW_PHONES[0];
        const layout = previewLayout(phone, { title, tileCount: tiles.length });
        const screenStyle = {
            '--phone-w': String(phone.width),
            '--pad-top': String(phone.insetTop + SCREEN_LAYOUT.paddingVertical),
            '--pad-bottom': String(phone.insetBottom + SCREEN_LAYOUT.paddingVertical),
            '--pad-x': String(SCREEN_LAYOUT.paddingHorizontal),
            '--tile-w': String(layout.tileWidth),
            '--tile-ratio': String(TILE_ASPECT_RATIO),
            '--tile-gap': String(TILE_GAP),
            '--title-size': String(SCREEN_LAYOUT.titleFontSize),
            '--title-line': String(SCREEN_LAYOUT.titleLineHeight),
            '--title-gap': String(SCREEN_LAYOUT.titleMarginBottom),
            '--title-max-lines': String(SCREEN_LAYOUT.titleMaxLines),
            '--tile-name-size': String(SCREEN_LAYOUT.tileNameFontSize),
            '--tile-name-pad': String(SCREEN_LAYOUT.tileNamePadding)
        };
        if (draft.backgroundImage) screenStyle.backgroundImage = `url(${JSON.stringify(draft.backgroundImage)})`;
        const nameOf = (id) => (known[id] && known[id].name) || id;
        return (
            <div className="admin-chooser-preview">
                <p className="admin-eyebrow">Preview</p>
                <div className="admin-chooser-phone-pick" role="radiogroup" aria-label="Preview on">
                    {PREVIEW_PHONES.map(option => (
                        <label key={option.id} className="admin-chooser-phone-option">
                            <input
                                type="radio"
                                name="chooserPreviewPhone"
                                value={option.id}
                                checked={option.id === phone.id}
                                onChange={() => this.setState({ previewPhone: option.id })}
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
                <div className="admin-chooser-phone" style={{ aspectRatio: `${phone.width} / ${phone.height}` }}>
                    <div className="admin-chooser-screen" style={screenStyle} data-phone={phone.id}>
                        {/* Centred when it fits, scrolling from the top when it
                            does not — the app's ScrollView with flexGrow 1. */}
                        <div className="admin-chooser-screen-content">
                            <p className="admin-chooser-screen-title">{title}</p>
                            {tiles.map(button => (
                                <div key={button.id} className="admin-chooser-screen-tile">
                                    {button.imageUrl
                                        ? <img src={button.imageUrl} alt={nameOf(button.id)} />
                                        : <span>{nameOf(button.id)}</span>}
                                </div>
                            ))}
                            {tiles.length < MIN_BUTTONS &&
                                <p className="admin-chooser-screen-note">Fans skip this screen until two buttons lead to live experiences.</p>
                            }
                        </div>
                    </div>
                </div>
                <p className="admin-hint" style={{ marginTop: 8 }}>
                    Drafts are left out, as they are in the app.
                    {layout.scrolls ? ' On this phone the buttons do not all fit, so fans scroll to reach the last.' : ''}
                    {' '}Shorter phones trim the edges of the background.
                </p>
            </div>
        );
    }

    render() {
        const known = this.props.experiences || {};
        const { status, draft, orphaned, flags, access, loginActive, saving, uploading, addChoice } = this.state;
        const ready = status === 'ready';
        const locked = !ready || saving;
        const dirty = this.hasUnsavedChanges();
        const saveable = this.canSave();
        const who = flags ? chooserStatus({ flags, access, loginActive }) : null;
        const line = this.statusLine(who);
        const warnings = ready ? chooserWarnings({ status: who, buttons: draft.buttons, experiences: known }) : [];
        const options = addableExperiences(known, draft.buttons);
        const landing = landingExperience(known);

        return (
            <section className="admin-chooser" aria-labelledby="chooserHeading">
                <div className="admin-list-head">
                    <h3 id="chooserHeading">Choose your Experience screen</h3>
                </div>
                <p className="admin-page-sub admin-chooser-sub">
                    A full-screen page of big image buttons, one per experience, that fans see after they sign in.
                    The button they tap becomes their experience; they can still change it later from the side menu.
                </p>

                <div className="admin-work">
                    <div className="admin-form-box">
                        {status === 'failed' &&
                            <div className="admin-callout is-warn" role="alert">
                                <p className="admin-callout-title">Could not load this screen's setup</p>
                                <ul>
                                    <li>Nothing here can be saved until it loads, so nothing already set up is overwritten. Refresh to try again.</li>
                                    <li>If it keeps failing, this club's database rules may not have been deployed since the screen was added.</li>
                                </ul>
                            </div>
                        }

                        <fieldset className="admin-fieldset">
                            <legend className="admin-legend">Who sees it</legend>
                            <div className="admin-chooser-status">
                                <span className={`admin-status-pill${who && who.state !== 'off' ? ' is-live' : ''}`}>{line.label}</span>
                                {line.detail && <span className="admin-hint" style={{ margin: 0 }}>{line.detail}</span>}
                                <a href="/featureaccess">Change this in Feature Access</a>
                            </div>
                            <p className="admin-hint">
                                Switched on and off in Feature Access, as <b>Choose Experience screen</b>. Fans get it when
                                they sign in, and once on each phone if they were already signed in (not if they had
                                already picked an experience from the side menu). It only appears
                                alongside Change Experience, and only when at least two buttons below lead to live experiences.
                            </p>
                            {warnings.length > 0 &&
                                <div className="admin-callout is-warn">
                                    <p className="admin-callout-title">Before fans see it as set up here</p>
                                    <ul>
                                        {warnings.map(warning => <li key={warning.id}>{warning.text}</li>)}
                                    </ul>
                                </div>
                            }
                            {orphaned.length > 0 &&
                                <p className="admin-note">
                                    {orphaned.length === 1 ? 'A saved button points' : `${orphaned.length} saved buttons point`} at an
                                    experience that no longer exists ({orphaned.join(', ')}). Fans never see {orphaned.length === 1 ? 'it' : 'them'};
                                    Save clears {orphaned.length === 1 ? 'it' : 'them'} out, so a new experience that
                                    reuses the key does not inherit {orphaned.length === 1 ? 'it' : 'them'}.
                                </p>
                            }
                            {landing &&
                                <p className="admin-note">
                                    Fans who have not picked one yet — including any this screen is not shown to — are in
                                    &ldquo;{landing.name}&rdquo;, the {landing.isPrimary ? 'primary' : 'default'} experience.
                                </p>
                            }
                        </fieldset>

                        <fieldset className="admin-fieldset">
                            <legend className="admin-legend">Title and background</legend>
                            <div className="form-group">
                                <label htmlFor="chooserTitle">Title</label>
                                <p className="admin-hint">Shown above the buttons. Leave it blank for &ldquo;{DEFAULT_TITLE}&rdquo;.</p>
                                <input
                                    type="text"
                                    id="chooserTitle"
                                    className="form-control"
                                    maxLength={TITLE_MAX}
                                    placeholder={DEFAULT_TITLE}
                                    value={draft.title}
                                    disabled={locked}
                                    onChange={(e) => { const title = e.target.value; this.updateDraft(() => ({ title })); }}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="chooserBackground">Background image</label>
                                <p className="admin-hint">
                                    {BACKGROUND_ART.width} &times; {BACKGROUND_ART.height} px, JPG up to 2 MB. Drawn full-screen; edges are trimmed on shorter
                                    phones, so keep important content away from them.
                                </p>
                                <div className="admin-drop-row">
                                    {draft.backgroundImage &&
                                        <img src={draft.backgroundImage} alt="" className="admin-chooser-bg-thumb" />
                                    }
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Dropzone
                                            onDrop={(accepted, rejected) => this.uploadInto(BACKGROUND, accepted, rejected,
                                                (_draft, url) => ({ backgroundImage: url }))}
                                            accept={IMAGE_ACCEPT}
                                            multiple={false}
                                            maxSize={UPLOAD_MAX_BYTES}
                                            disabled={locked || !!uploading}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <section className="dropzone-container">
                                                    <div {...getRootProps({ className: 'dropzone' })}>
                                                        <input {...getInputProps()} id="chooserBackground" />
                                                        <p>
                                                            {uploading === BACKGROUND
                                                                ? 'Uploading…'
                                                                : (draft.backgroundImage
                                                                    ? 'Drop a new image here, or click to replace it'
                                                                    : 'Drop an image here, or click to choose one')}
                                                        </p>
                                                    </div>
                                                </section>
                                            )}
                                        </Dropzone>
                                    </div>
                                    {draft.backgroundImage &&
                                        <button type="button" className="btn btn-sm btn-secondary" disabled={locked}
                                                onClick={() => this.updateDraft(() => ({ backgroundImage: '' }))}>
                                            Remove
                                        </button>
                                    }
                                </div>
                            </div>
                        </fieldset>

                        <fieldset className="admin-fieldset">
                            <legend className="admin-legend">Buttons</legend>
                            <p className="visually-hidden" role="status" aria-live="polite">{this.state.announcement}</p>
                            <p className="admin-hint">
                                {TILE_ART.width} &times; {TILE_ART.height} px (7:5), JPG or PNG up to 2 MB. The image is the whole button, including
                                its colour; fans see it uncropped. Click an image, or drop a file on it, to replace it.
                            </p>
                            {draft.buttons.length === 0 && ready &&
                                <p className="admin-hint" style={{ fontStyle: 'italic' }}>No buttons yet. Add one for each experience fans can pick.</p>
                            }
                            {draft.buttons.length > 0 &&
                                <div className="admin-tile-grid admin-chooser-grid">
                                    {draft.buttons.map((button, i) => this.renderTile(button, i, draft.buttons.length, locked))}
                                </div>
                            }
                            {options.length > 0 &&
                                <div className="form-group admin-chooser-add">
                                    <label htmlFor="chooserAddButton">Add a button</label>
                                    <div className="admin-chooser-add-row">
                                        <select
                                            id="chooserAddButton"
                                            className="form-control"
                                            value={addChoice}
                                            disabled={locked}
                                            onChange={(e) => this.setState({ addChoice: e.target.value })}
                                        >
                                            <option value="">Choose an experience…</option>
                                            {options.map(option => (
                                                <option key={option.id} value={option.id}>{option.label}</option>
                                            ))}
                                        </select>
                                        <button type="button" className="btn btn-secondary" disabled={locked || !addChoice} onClick={this.handleAdd}>
                                            Add button
                                        </button>
                                    </div>
                                </div>
                            }
                        </fieldset>

                        {this.state.conflict &&
                            <div className="admin-callout is-warn" role="alert">
                                <p className="admin-callout-title">Not saved</p>
                                <ul>
                                    <li>{CONFLICT_MESSAGE}</li>
                                    <li>Reloading replaces what is on screen with their version, so note anything of yours you want to keep first.</li>
                                </ul>
                            </div>
                        }
                        <div className="admin-actions">
                            {/* Not mid-upload either: saving then would store the
                                screen without the image the operator is waiting on. */}
                            <button type="button" className="btn btn-primary" disabled={!saveable || locked || !!uploading} onClick={this.save}>
                                {saving ? 'Saving…' : 'Save screen'}
                            </button>
                            {dirty && <span className="admin-unsaved">You have unsaved changes</span>}
                        </div>
                    </div>

                    <div className="admin-side">
                        {this.renderPreview(draft, known)}
                    </div>
                </div>
            </section>
        );
    }
}

// How long an image upload may run before the editor gives up on it. A prop
// so a test can shorten it; the Experiences page leaves it at the default.
ExperienceChooserSetup.defaultProps = { uploadTimeoutMs: UPLOAD_TIMEOUT_MS };

export default ExperienceChooserSetup;
